import { capitalize } from 'lodash';
import { View } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@apollo/client/react';

import { ActivationBanner as Banner } from '@/components/ui/activation-banner';
import { CircularProgress } from '@/components/ui/circular-progress';
import { useSaasEscrow } from 'src/context/saas-escrow';
import { Button } from '@/components/ui/button';
import { EscrowStatusEnum, InvitationStatusEnum } from 'src/graphql/schema';
import { ClientChangesRequestedDocument } from 'src/graphql/actions/suggestChanges/clientChangesRequested.query';
import { OtherEscrowStatusEnum } from 'src/shared/constants';
import { getActiveCertification } from 'src/utils/escrow';

import ApproveSignatureDialog from './ApproveSignatureDialog';
import ActivateEscrowDialog from './ActivateEscrowDialog';

const GetCertificateButton = () => {
  const navigate = useNavigate();
  const {
    saasEscrow: { id, certificationCertified, certificationVerified, activatedAt },
  } = useSaasEscrow();

  const type = getActiveCertification({ certificationCertified, certificationVerified, activatedAt });

  if (!type) return null;

  const onViewCertificate = () => {
    navigate(`/saas-escrows/${id}/overview`, {
      state: { scrollToCertificate: true },
    });
  };

  return (
    <Button variant="secondary" size="sm" onClick={onViewCertificate}>
      <View /> View certificate
    </Button>
  );
};

const actionBannerMap = {
  [EscrowStatusEnum.AdminReview]: {
    title: 'We’re setting up your escrow agreement.',
    description: 'As soon as it’s ready, we’ll notify you to review it.',
    action: null,
    progress: 15,
  },
  [EscrowStatusEnum.Onboarding]: {
    title: 'Ready for review',
    description:
      'Your agreement is ready. Review the details in the Agreement tab, and submit it for signature when you’re ready.',
    action: ApproveSignatureDialog,
    progress: 35,
  },
  [OtherEscrowStatusEnum.AwaitingSignature]: {
    title: 'Sent for signature',
    description: 'Signatories will receive an e-signature request. Edits are locked until all parties sign.',
    action: null,
    progress: 55,
  },
  [OtherEscrowStatusEnum.SignedDepositor]: {
    title: 'Signed – Ready for activation',
    description: 'Deposit your assets to activate this escrow.',
    action: ActivateEscrowDialog,
    progress: 75,
  },
  [OtherEscrowStatusEnum.SignedBeneficiary]: {
    title: 'Signed – Ready for activation',
    description: 'The escrow agreement has been signed. The depositor can now deposit assets and activate the escrow.',
    action: null,
    progress: 75,
  },
  [OtherEscrowStatusEnum.ActivationProcess]: {
    title: 'Pending activation',
    description:
      "Once the system marks all your assets as Taken, your escrow activates automatically. Check the Deposits tab to review each asset's status.",
    action: null,
    progress: 90,
  },
  [EscrowStatusEnum.Active]: {
    title: 'Your escrow is active and validated!',
    description:
      'This escrow is now active and has earned a validated badge. Download the escrow certificate as a confirmation of the active status.',
    action: GetCertificateButton,
    progress: 0,
  },
};

const ActivationBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const {
    saasEscrow,
    saasEscrow: { pendingActivation, agreement, currentCompanyRole, escrowInvitation, escrowTermination },
  } = useSaasEscrow();
  const { data: changesRequested } = useQuery(ClientChangesRequestedDocument);

  let status: EscrowStatusEnum | OtherEscrowStatusEnum = saasEscrow.status;
  if (pendingActivation) status = OtherEscrowStatusEnum.ActivationProcess;
  if (saasEscrow.status === EscrowStatusEnum.Onboarding && agreement.approved)
    status = OtherEscrowStatusEnum.AwaitingSignature;
  if (status === EscrowStatusEnum.Signed)
    status = OtherEscrowStatusEnum[`Signed${capitalize(currentCompanyRole!)}` as const];

  const bannerConfig = actionBannerMap[status as keyof typeof actionBannerMap];

  if (
    !bannerConfig ||
    changesRequested?.clientChangesRequested.isRequested ||
    !isVisible ||
    escrowInvitation?.status === InvitationStatusEnum.Rejected ||
    Boolean(escrowTermination)
  )
    return null;

  const { title, description, action: Action, progress } = bannerConfig;

  const onClose = () => setIsVisible(false);

  return (
    <Banner
      title={title}
      description={description}
      progress={Boolean(progress) && <CircularProgress percentage={progress} size={60} />}
      action={Action && <Action />}
      onClose={status === EscrowStatusEnum.Active ? onClose : undefined}
    />
  );
};

export default ActivationBanner;
